import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  styled,
  Paper,
  TableSortLabel,
  Button,
  Grid,
  Typography,
  Popover,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";
import { convertEpochToDateDMY } from "../../../utils";
import { ArrowDownward } from "@mui/icons-material";
import { BackButton } from "@egovernments/digit-ui-react-components";

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  backgroundColor: "#1f4a7c", // Dark blue background
  color: theme.palette.common.white,
  "& .MuiTableSortLabel-root": {
    color: "white",
    "&:hover": {
      color: "white",
    },
    "&.Mui-active": {
      color: "white",
    },
  },
  "& .MuiInput-root": {
    "&:before": {
      borderBottomColor: "rgba(255, 255, 255, 0.42)",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "white",
    },
    "&:after": {
      borderBottomColor: "white",
    },
    "& input": {
      color: "white", // White text for input
      "&::placeholder": {
        color: "rgba(255, 255, 255, 0.7)",
      },
    },
  },
  "& .MuiSvgIcon-root": {
    color: "white",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const SmallTableCell = styled(TableCell)({
  fontSize: "0.75rem", // Smaller font size
  padding: "8px 12px", // Compact padding
});

const getApplicationUrl = (applicationNo) => {
  if (!applicationNo) return "#";

  // Split the application number by slashes
  const parts = applicationNo.split("/");

  // Check the third part (index 2) to determine the type
  const typeCode = parts[2];

  switch (typeCode) {
    case "MS":
      return `/digit-ui/citizen/obps/structuralform12/${applicationNo}`;
    case "LU":
      return `/digit-ui/citizen/obps/structural/formLandUse/${applicationNo}`;
    case "PF":
      return `/digit-ui/citizen/obps/structural/PURCHASABLE_FAR/${applicationNo}`;
    default:
      return "#"; // Fallback URL
  }
};

const EnhancedTable = ({ data, isLoading, tenantId }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [globalSearch, setGlobalSearch] = useState("");

  // State for sorting
  const [orderBy, setOrderBy] = useState("applicationDate");
  const [order, setOrder] = useState("desc");

  // State for column filters
  const [filters, setFilters] = useState({
    name: "",
    architect: "",
    plotArea: "",
    mobileNumber: "",
    applicationNo: "",
    applicationType: "",
    status: "",
    date: "",
    schemeName: "",
  });

  // State for column visibility
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([
    "srNo",
    "name",
    // "architect",
    "mobileNumber",
    "applicationNo",
    // "plotArea",
    "applicationType",
    // "schemeName",
    "applicationDate",
    "status",
  ]);
  useEffect(() => {
    Digit.SessionStorage.del("OWNER_INFO");
    Digit.SessionStorage.del("FASTPAS_ID");
    Digit.SessionStorage.del("FILE_NO");
    Digit.SessionStorage.del("EDCR_CREATE");
    Digit.SessionStorage.del("FORM_MAP_SUBMISSION");
    Digit.SessionStorage.del("FORM_SELF_RESIDENTIAL");
    Digit.SessionStorage.del("SELF_CURRENT_TAB");
    Digit.SessionStorage.del("FORM_KYP");
    Digit.SessionStorage.del("FORM_SELF_APPROVAL");
    if (data?.length > 0) {
      Digit.SessionStorage.set("FILE_COUNT", data?.length);
    }
  }, []);
  // Column configuration
  const allColumns = [
    {
      id: "srNo",
      label: "Sr No",
      filterable: false,
      sortable: false,
      render: (item, index) => page * rowsPerPage + index + 1,
      width: "5%",
    },
    {
      id: "name",
      label: "Name",
      filterable: true,
      sortable: true,
      render: (item) => item?.landInfo?.owners?.[0]?.name || "-",
      width: "12%",
    },
    // {
    //   id: "architect",
    //   label: "Architect",
    //   filterable: true,
    //   sortable: true,
    //   render: (item) => item?.professionaldetails?.name || "-",
    //   width: "12%",
    // },
    {
      id: "mobileNumber",
      label: "Mobile No.",
      filterable: true,
      sortable: true,
      render: (item) => item?.landInfo?.owners?.[0]?.mobileNumber || "-",
      width: "10%",
    },
    {
      id: "applicationNo",
      label: "Application No.",
      filterable: true,
      sortable: true,
      render: (item) => (
        <strong
          onClick={(e) => {
            e.preventDefault();
            window.location.href = getApplicationUrl(item?.applicationNo);
            Digit.SessionStorage.set("OWNER_INFO", item?.landInfo?.owners?.[0]?.name);
            Digit.SessionStorage.set("FASTPAS_ID", item);
            Digit.SessionStorage.set("FILE_NO", item?.applicationNo);
          }}
          style={{ color: "#007bff", textDecoration: "none", cursor: "pointer" }}
        >
          {item?.applicationNo || "-"}
        </strong>
      ),
      width: "10%",
    },
    // {
    //   id: "plotArea",
    //   label: "Plot Area",
    //   filterable: true,
    //   sortable: true,
    //   render: (item) => item?.plotArea || "-",
    //   width: "8%",
    // },
    {
      id: "applicationType",
      label: "Application Type",
      filterable: true,
      sortable: true,
      render: (item) => t(item?.applicationType) || "-",
      width: "10%",
    },
    // {
    //   id: "schemeName",
    //   label: "Scheme",
    //   filterable: true,
    //   sortable: true,
    //   render: (item) => item?.schemeName || "-",
    //   width: "10%",
    // },
    {
      id: "applicationDate",
      label: "Date",
      filterable: false,
      sortable: true,
      render: (item) => convertEpochToDateDMY(item?.auditDetails?.createdTime) || "-",
      width: "10%",
    },
    {
      id: "status",
      label: "Status",
      filterable: true,
      sortable: true,
      render: (item) => {
        const role = item?.assignes?.[0]?.roles?.find((role) => role.name !== "Employee")?.name;

        if (item?.assignes?.length > 0) {
          return `Pending with [${role}] (${item?.assignes?.[0].name})` || `-`;
        }
        return t(`WF_BPA_MAP_${item?.status}`) || `-`;
      },
      width: "13%",
    },
  ];

  // Filter columns based on selection
  const columns = allColumns.filter((col) => selectedColumns.includes(col.id));

  // Handle column selection change
  const handleColumnToggle = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      // Don't allow deselecting all columns - at least one must remain
      if (selectedColumns.length > 1) {
        setSelectedColumns(selectedColumns.filter((id) => id !== columnId));
      }
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };

  // Open column filter popover
  const handleOpenColumnFilter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close column filter popover
  const handleCloseColumnFilter = () => {
    setAnchorEl(null);
  };

  // Improved sorting function
  const getComparator = (order, orderBy) => {
    return (a, b) => {
      // Extract values based on orderBy
      const getValue = (item) => {
        switch (orderBy) {
          case "name":
            return item?.landInfo?.owners?.[0]?.name?.toLowerCase() || "";
          // case "architect":
          //   return item?.professionaldetails?.name?.toLowerCase() || "";
          case "mobileNumber":
            return parseInt(item?.landInfo?.owners?.[0]?.mobileNumber) || 0;
          case "applicationNo":
            return item?.applicationNo?.toLowerCase() || "";
          // case "plotArea":
          //   return parseFloat(item?.plotArea) || 0;
          case "applicationType":
            return item?.applicationType?.toLowerCase() || "";
          // case "schemeName":
          //   return item?.schemeName?.toLowerCase() || "";
          case "applicationDate":
            return item?.auditDetails?.createdTime || 0;
          case "status":
            return t(`WF_BPA_MAP_${item?.status}`)?.toLowerCase() || "";
          default:
            return "";
        }
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (order === "desc") {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    };
  };

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter change
  const handleFilterChange = (property, value) => {
    setFilters({
      ...filters,
      [property]: value,
    });
    setPage(0);
  };

  // Handle clear filter
  const handleClearFilter = (property) => {
    setFilters({
      ...filters,
      [property]: "",
    });
  };

  // Handle global search
  const handleGlobalSearch = (value) => {
    setGlobalSearch(value);
    setPage(0);
  };

  // Check if value matches search term
  const matchesSearch = (value, search) => {
    if (!value) return false;
    return value.toString().toLowerCase().includes(search.toLowerCase());
  };

  // Apply filtering and global search
  const getFilteredData = () => {
    let filteredData = data || [];

    // Apply global search first
    if (globalSearch) {
      filteredData = filteredData?.filter((item) => {
        return (
          matchesSearch(item?.landInfo?.owners?.[0]?.name, globalSearch) ||
          matchesSearch(item?.professionaldetails?.name, globalSearch) ||
          matchesSearch(item?.plotArea, globalSearch) ||
          matchesSearch(item?.landInfo?.owners?.[0]?.mobileNumber, globalSearch) ||
          matchesSearch(item?.applicationNo, globalSearch) ||
          matchesSearch(item?.applicationType, globalSearch) ||
          matchesSearch(item?.status, globalSearch) ||
          matchesSearch(item?.schemeName, globalSearch) ||
          matchesSearch(convertEpochToDateDMY(item?.auditDetails?.createdTime), globalSearch)
        );
      });
    }

    // Apply column filters
    if (filters.name) {
      filteredData = filteredData.filter((item) => matchesSearch(item?.landInfo?.owners?.[0]?.name, filters.name));
    }

    // if (filters.architect) {
    //   filteredData = filteredData.filter((item) => matchesSearch(item?.professionaldetails?.name, filters.architect));
    // }

    // if (filters.plotArea) {
    //   filteredData = filteredData.filter((item) => matchesSearch(item?.plotArea?.toString(), filters.plotArea));
    // }

    if (filters.mobileNumber) {
      filteredData = filteredData.filter((item) => matchesSearch(item?.landInfo?.owners?.[0]?.mobileNumber, filters.mobileNumber));
    }

    if (filters.applicationNo) {
      filteredData = filteredData.filter((item) => matchesSearch(item?.applicationNo, filters.applicationNo));
    }

    if (filters.applicationType) {
      filteredData = filteredData.filter((item) => matchesSearch(item?.applicationType, filters.applicationType));
    }

    // if (filters.schemeName) {
    //   filteredData = filteredData.filter((item) => matchesSearch(item?.schemeName, filters.schemeName));
    // }

    if (filters.status) {
      filteredData = filteredData.filter((item) => matchesSearch(item?.status, filters.status));
    }

    return filteredData;
  };

  const filteredData = getFilteredData();

  // Apply sorting
  const filteredAndSortedData = filteredData.slice().sort(getComparator(order, orderBy));

  const paginatedData = filteredAndSortedData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
    <div style={{ padding: "0px 4px" }}>
      <TableContainer className="citizen-table-container">
        <BackButton />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <caption className="citizen-table-caption">My Applications</caption>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              variant="outlined"
              className="bg-white"
              startIcon={<FilterListIcon />}
              onClick={handleOpenColumnFilter}
              size="small"
              sx={{
                height: "27px",
                fontSize: "12px",
                borderColor: "rgb(31, 74, 124)",
                color: "rgb(31, 74, 124)",
              }}
            >
              {t("FASTPAS.GLOBAL.FILTERS.TEXT")}({selectedColumns.length}/{allColumns.length})
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search here..."
              value={globalSearch}
              onChange={(e) => handleGlobalSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ background: "#1f4a7c", color: "#fff", height: "24px", borderRadius: "7px 0px 0px 7px", width: "29px" }} />
                  </InputAdornment>
                ),
                endAdornment: globalSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleGlobalSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                padding: "0px",
                height: "25px",
                "& .MuiInputBase-root": {
                  height: "27px",
                  padding: "0px",
                  backgroundColor: '#fff'
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgb(31, 74, 124)",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  fontSize: "12px",
                },
              }}
            />
          </div>
        </div>

        {/* Column selection popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseColumnFilter}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <div className="select-column-popover">
            <Typography variant="subtitle2" gutterBottom>
              Select Columns to Display
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ padding: "0px" }}
                  checked={selectedColumns.length === allColumns.length}
                  indeterminate={selectedColumns.length > 0 && selectedColumns.length < allColumns.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns(allColumns.map((col) => col.id));
                    } else {
                      // Optionally, keep at least one column selected
                      setSelectedColumns([allColumns[0].id]);
                    }
                  }}
                />
              }
              sx={{ margin: "0px", fontWeight: "bold" }}
              label="Select All"
            />
            {allColumns.map((column) => (
              <div key={column.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ padding: "0px" }}
                      checked={selectedColumns.includes(column.id)}
                      onChange={() => handleColumnToggle(column.id)}
                      disabled={column.id === "srNo" && selectedColumns.length === 1}
                    />
                  }
                  sx={{ margin: "0px" }}
                  label={column.label}
                />
              </div>
            ))}
          </div>
        </Popover>

        <Table stickyHeader aria-label="enhanced table" size="small" className="citizen-application-table" style={{
          tableLayout: "fixed",
          width: "100%",
        }}>
          <thead>
            <tr className="citizen-header-row">
              {columns.map((column) => (
                <td key={column.id}
                  style={{ width: column.width }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    {column.label}
                    {column.sortable && (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                        sx={{
                          color: "white !important",
                          "& .MuiTableSortLabel-icon": {
                            opacity: "1 !important",
                            color: "white !important",
                          },
                          "&:hover": {
                            color: "white !important",
                          },
                        }}
                        IconComponent={(props) => (
                          <ArrowDownward
                            {...props}
                            sx={{
                              color: "white !important",
                              opacity: 1,
                            }}
                          />
                        )}
                      />
                    )}
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              {columns?.map((column) => (
                <td key={column.id}>
                  {column.filterable && (
                    <TextField
                      variant="standard"
                      size="small"
                      value={filters[column.id] || ""}
                      onChange={(e) => handleFilterChange(column.id, e.target.value)}
                      InputProps={{
                        endAdornment: filters[column.id] && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => handleClearFilter(column.id)} sx={{ color: "white" }}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInput-input": {
                          color: "black !important",
                          fontSize: "0.75rem",
                        },
                        width: "100%",
                      }}
                    />
                  )}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} align="center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} align="center">
                  No records found
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr hover key={index}>
                  {columns.map((column) => (
                    <td key={`${index}-${column.id}`}
                      // style={{ width: column.width }}
                      style={{
                        width: column.width,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        verticalAlign: "top",
                      }}
                    >
                      {column.render(item, index)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredAndSortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default EnhancedTable;
